/**
 * Singleton Pattern Implementation
 *
 * Provides a default singleton instance with convenient wrapper functions
 * for simple use cases, plus createInstance() for advanced scenarios.
 */

import { ExperienceRuntime } from './runtime';
import type { Context, Decision, Experience, ExperienceConfig, RuntimeState } from './types';

/**
 * Create a new Experience SDK instance
 *
 * Use this for advanced scenarios where you need multiple isolated runtimes.
 *
 * @example
 * ```typescript
 * import { createInstance } from '@prosdevlab/experience-sdk';
 *
 * const exp = createInstance({ debug: true });
 * await exp.init();
 * exp.register('welcome', { ... });
 * ```
 */
export function createInstance(config?: ExperienceConfig): ExperienceRuntime {
  return new ExperienceRuntime(config);
}

/**
 * Default singleton instance
 *
 * Provides a convenient global instance for simple use cases.
 * For script tag users, this is exposed as the global `experiences` object.
 */
const defaultInstance = createInstance();

/**
 * Initialize the Experience SDK
 *
 * @example
 * ```typescript
 * import { init } from '@prosdevlab/experience-sdk';
 * await init({ debug: true });
 * ```
 */
export async function init(config?: ExperienceConfig): Promise<void> {
  return defaultInstance.init(config);
}

/**
 * Register an experience
 *
 * @example
 * ```typescript
 * import { register } from '@prosdevlab/experience-sdk';
 *
 * register('welcome-banner', {
 *   type: 'banner',
 *   targeting: { url: { contains: '/' } },
 *   content: { title: 'Welcome!', message: 'Thanks for visiting' }
 * });
 * ```
 */
export function register(id: string, experience: Omit<Experience, 'id'>): void {
  defaultInstance.register(id, experience);
}

/**
 * Evaluate experiences against current context
 * First match wins (use evaluateAll() for multiple experiences)
 *
 * @example
 * ```typescript
 * import { evaluate } from '@prosdevlab/experience-sdk';
 *
 * const decision = evaluate({ url: window.location.href });
 * if (decision.show) {
 *   console.log('Show experience:', decision.experienceId);
 *   console.log('Reasons:', decision.reasons);
 * }
 * ```
 */
export function evaluate(context?: Partial<Context>): Decision {
  return defaultInstance.evaluate(context);
}

/**
 * Evaluate all experiences against current context
 * Returns array of decisions sorted by priority (higher = more important)
 * All matching experiences will be shown
 *
 * @example
 * ```typescript
 * import { evaluateAll } from '@prosdevlab/experience-sdk';
 *
 * const decisions = evaluateAll({ url: window.location.href });
 * decisions.forEach(decision => {
 *   if (decision.show) {
 *     console.log('Show:', decision.experienceId);
 *     console.log('Reasons:', decision.reasons);
 *   }
 * });
 * ```
 */
export function evaluateAll(context?: Partial<Context>): Decision[] {
  return defaultInstance.evaluateAll(context);
}

/**
 * Explain why a specific experience would/wouldn't show
 *
 * @example
 * ```typescript
 * import { explain } from '@prosdevlab/experience-sdk';
 *
 * const explanation = explain('welcome-banner');
 * console.log('Would show?', explanation?.show);
 * console.log('Reasons:', explanation?.reasons);
 * ```
 */
export function explain(experienceId: string): Decision | null {
  return defaultInstance.explain(experienceId);
}

/**
 * Get current runtime state
 *
 * @example
 * ```typescript
 * import { getState } from '@prosdevlab/experience-sdk';
 *
 * const state = getState();
 * console.log('Initialized?', state.initialized);
 * console.log('Experiences:', Array.from(state.experiences.keys()));
 * ```
 */
export function getState(): RuntimeState {
  return defaultInstance.getState();
}

/**
 * Subscribe to SDK events
 *
 * @example
 * ```typescript
 * import { on } from '@prosdevlab/experience-sdk';
 *
 * const unsubscribe = on('experiences:evaluated', (decision) => {
 *   console.log('Evaluation:', decision);
 * });
 *
 * // Later: unsubscribe()
 * ```
 */
export function on(event: string, handler: (...args: unknown[]) => void): () => void {
  return defaultInstance.on(event, handler);
}

/**
 * Destroy the SDK instance
 *
 * @example
 * ```typescript
 * import { destroy } from '@prosdevlab/experience-sdk';
 * await destroy();
 * ```
 */
export async function destroy(): Promise<void> {
  return defaultInstance.destroy();
}

/**
 * Default export for convenient importing
 *
 * @example
 * ```typescript
 * import experiences from '@prosdevlab/experience-sdk';
 * await experiences.init();
 * ```
 *
 * For IIFE builds (script tag), this becomes window.experiences via default export
 */
const experiencesObject = {
  createInstance,
  init,
  register,
  evaluate,
  evaluateAll,
  explain,
  getState,
  on,
  destroy,
};

const experiencesProxy = new Proxy(experiencesObject, {
  get(target, prop) {
    // Check wrapper functions first
    if (prop in target && target[prop as keyof typeof target]) {
      return target[prop as keyof typeof target];
    }
    // Fall back to SDK instance for plugin APIs (modal, inline, banner, etc.)
    return (defaultInstance.sdk as any)[prop];
  },
});

export { experiencesProxy as experiences };
